import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import {
    ArrowLeft,
    ArrowRight,
    Camera,
    Upload,
    Loader2,
    CheckCircle,
    Hash,
    Users,
    Tag,
    Save,
    ZoomIn,
    ZoomOut,
    RotateCw,
    Maximize,
    Zap
} from 'lucide-react';
import { useLocation } from "wouter";
import { PhotoService } from '../services/photoService';
import { useUploadPhotos } from '../hooks/usePhotos';
import type { PhotoUploadData, PhotoUploadProgress } from '../models/photo.types';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface ImageMetadata {
    originalName: string;
    technicalData: {
        camera?: string;
        resolution?: string;
        quality?: string;
        iso?: string;
        aperture?: string;
        shutterSpeed?: string;
        focalLength?: string;
        dateTime?: string;
        gps?: string;
    };
    fileInfo: {
        size: number;
        type: string;
        lastModified: number;
    };
    suggestedAttributes: {
        bibNumbers?: string[];
        tags?: string[];
        category?: string;
        description?: string;
    };
}

interface UploadFile extends File {
    id: string;
    preview: string;
    metadata?: ImageMetadata;
    processed?: boolean;
    processing?: boolean;
}

export default function PhotoViewer() {
    const [files, setFiles] = useState<UploadFile[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedEventId, setSelectedEventId] = useState<string>('');
    const [category, setCategory] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [zoom, setZoom] = useState(100);
    const { toast } = useToast();
    const [, navigate] = useLocation();

    // Hook do módulo Photos
    const uploadPhotosMutation = useUploadPhotos();

    // Load data from sessionStorage
    useEffect(() => {
        const storedFiles = sessionStorage.getItem('photoViewerFiles');
        const storedEvent = sessionStorage.getItem('photoViewerEvent');
        const storedCategory = sessionStorage.getItem('photoViewerCategory');
        const storedDescription = sessionStorage.getItem('photoViewerDescription');

        if (storedFiles) {
            const parsedFiles = JSON.parse(storedFiles);
            setFiles(parsedFiles);
        } else {
            // If no files, redirect back to selector
            navigate('/photo-selector');
            return;
        }

        if (storedEvent) setSelectedEventId(storedEvent);
        if (storedCategory) setCategory(storedCategory);
        if (storedDescription) setDescription(storedDescription);
    }, [navigate]);

    const currentFile = files[currentIndex];

    const goToPrevious = () => {
        setCurrentIndex(prev => prev > 0 ? prev - 1 : files.length - 1);
        setZoom(100);
    };

    const goToNext = () => {
        setCurrentIndex(prev => prev < files.length - 1 ? prev + 1 : 0);
        setZoom(100);
    };

    const updateCurrentFileMetadata = (field: string, value: string) => {
        setFiles(prev => prev.map((file, index) => {
            if (index === currentIndex) {
                const updatedFile = { ...file };
                if (!updatedFile.metadata) {
                    updatedFile.metadata = {
                        originalName: file.name,
                        technicalData: {},
                        fileInfo: {
                            size: file.size,
                            type: file.type,
                            lastModified: file.lastModified
                        },
                        suggestedAttributes: {}
                    };
                }
                (updatedFile.metadata as any)[field] = value;
                return updatedFile;
            }
            return file;
        }));
    };

    const updateCurrentFileSuggestedAttributes = (field: string, value: any) => {
        setFiles(prev => prev.map((file, index) => {
            if (index === currentIndex) {
                const updatedFile = { ...file };
                if (!updatedFile.metadata) {
                    updatedFile.metadata = {
                        originalName: file.name,
                        technicalData: {},
                        fileInfo: {
                            size: file.size,
                            type: file.type,
                            lastModified: file.lastModified
                        },
                        suggestedAttributes: {}
                    };
                }
                if (!updatedFile.metadata.suggestedAttributes) {
                    updatedFile.metadata.suggestedAttributes = {};
                }
                (updatedFile.metadata.suggestedAttributes as any)[field] = value;
                return updatedFile;
            }
            return file;
        }));
    };

    const handleUpload = async () => {
        if (!selectedEventId || files.length === 0) {
            toast({
                title: "Erro",
                description: "Selecione um evento e pelo menos uma foto.",
                variant: "destructive",
            });
            return;
        }

        try {
            setIsUploading(true);
            setUploadProgress(0);

            const uploadData: PhotoUploadData = {
                files: files.map(f => f as File),
                eventId: selectedEventId,
                category,
                description,
                metadata: {
                    author: 'Fotógrafo',
                    tags: files.flatMap(f => f.metadata?.suggestedAttributes?.tags || [])
                }
            };

            const result = await uploadPhotosMutation.mutateAsync(uploadData);

            if (result.success) {
                toast({
                    title: "Sucesso!",
                    description: `${result.photos.length} foto(s) enviada(s) com sucesso!`,
                });

                // Clear session storage
                sessionStorage.removeItem('photoViewerFiles');
                sessionStorage.removeItem('photoViewerEvent');
                sessionStorage.removeItem('photoViewerCategory');
                sessionStorage.removeItem('photoViewerDescription');

                // Navigate to success page or dashboard
                navigate('/photographer-dashboard');
            } else {
                throw new Error('Upload failed');
            }
        } catch (error) {
            console.error('Upload error:', error);
            toast({
                title: "Erro no upload",
                description: "Não foi possível enviar as fotos. Tente novamente.",
                variant: "destructive",
            });
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (timestamp: number) => {
        return new Date(timestamp).toLocaleString('pt-BR');
    };

    if (!currentFile) {
        return (
            <div className="container mx-auto px-4 py-8">
                <Card>
                    <CardContent className="p-8 text-center">
                        <h2 className="text-2xl font-bold mb-4">Nenhuma foto encontrada</h2>
                        <p className="text-muted-foreground mb-4">
                            Volte ao seletor de fotos para continuar.
                        </p>
                        <Button onClick={() => navigate('/photo-selector')}>
                            Voltar ao Seletor
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Image Viewer */}
                <div className="lg:col-span-2 space-y-4">
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="flex items-center gap-2">
                                <Camera className="h-5 w-5" />
                                Visualizador de Fotos
                            </CardTitle>
                            <div className="flex items-center gap-2">
                                <Badge variant="outline">
                                    {currentIndex + 1} de {files.length}
                                </Badge>
                                <div className="flex gap-1">
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={goToPrevious}
                                        disabled={files.length <= 1}
                                    >
                                        <ArrowLeft className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={goToNext}
                                        disabled={files.length <= 1}
                                    >
                                        <ArrowRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="relative bg-gray-100 rounded-lg overflow-hidden">
                                <img
                                    src={currentFile.preview}
                                    alt={currentFile.name}
                                    className="w-full h-auto max-h-[600px] object-contain"
                                    style={{ transform: `scale(${zoom / 100})` }}
                                />

                                {/* Zoom Controls */}
                                <div className="absolute top-4 right-4 flex flex-col gap-2">
                                    <Button
                                        variant="secondary"
                                        size="icon"
                                        onClick={() => setZoom(prev => Math.min(prev + 25, 300))}
                                    >
                                        <ZoomIn className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        size="icon"
                                        onClick={() => setZoom(prev => Math.max(prev - 25, 25))}
                                    >
                                        <ZoomOut className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        size="icon"
                                        onClick={() => setZoom(100)}
                                    >
                                        <Maximize className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>

                            <div className="mt-4 flex items-center justify-between">
                                <div className="text-sm text-muted-foreground">
                                    Zoom: {zoom}%
                                </div>
                                <div className="text-sm text-muted-foreground">
                                    {currentFile.name}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Metadata Panel */}
                <div className="space-y-4">
                    {/* Upload Info */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Upload className="h-5 w-5" />
                                Informações do Upload
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div>
                                <Label htmlFor="event">Evento</Label>
                                <Input
                                    id="event"
                                    value={selectedEventId}
                                    onChange={(e) => setSelectedEventId(e.target.value)}
                                    placeholder="ID do evento"
                                />
                            </div>

                            <div>
                                <Label htmlFor="category">Categoria</Label>
                                <Input
                                    id="category"
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value)}
                                    placeholder="Ex: Corrida, Ciclismo"
                                />
                            </div>

                            <div>
                                <Label htmlFor="description">Descrição</Label>
                                <Textarea
                                    id="description"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Descrição geral das fotos"
                                    rows={3}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* File Info */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Hash className="h-5 w-5" />
                                Informações do Arquivo
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Nome:</span>
                                <span className="text-sm font-medium">{currentFile.name}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Tamanho:</span>
                                <span className="text-sm font-medium">{formatFileSize(currentFile.size)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Tipo:</span>
                                <span className="text-sm font-medium">{currentFile.type}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Modificado:</span>
                                <span className="text-sm font-medium">{formatDate(currentFile.lastModified)}</span>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Technical Data */}
                    {currentFile.metadata?.technicalData && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Camera className="h-5 w-5" />
                                    Dados Técnicos
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                {Object.entries(currentFile.metadata.technicalData).map(([key, value]) => (
                                    <div key={key} className="flex justify-between">
                                        <span className="text-sm text-muted-foreground capitalize">
                                            {key.replace(/([A-Z])/g, ' $1').trim()}:
                                        </span>
                                        <span className="text-sm font-medium">{value}</span>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    )}

                    {/* Suggested Attributes */}
                    {currentFile.metadata?.suggestedAttributes && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Tag className="h-5 w-5" />
                                    Atributos Sugeridos
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {currentFile.metadata.suggestedAttributes.bibNumbers && (
                                    <div>
                                        <Label>Números do Peito</Label>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {currentFile.metadata.suggestedAttributes.bibNumbers.map((bib, index) => (
                                                <Badge key={index} variant="secondary">
                                                    {bib}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {currentFile.metadata.suggestedAttributes.tags && (
                                    <div>
                                        <Label>Tags</Label>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {currentFile.metadata.suggestedAttributes.tags.map((tag, index) => (
                                                <Badge key={index} variant="outline">
                                                    {tag}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Upload Progress */}
                    {isUploading && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Loader2 className="h-5 w-5 animate-spin" />
                                    Enviando Fotos
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Progress value={uploadProgress} className="w-full" />
                                <p className="text-sm text-muted-foreground mt-2">
                                    {uploadProgress}% concluído
                                </p>
                            </CardContent>
                        </Card>
                    )}

                    {/* Upload Button */}
                    <Button
                        onClick={handleUpload}
                        disabled={isUploading || !selectedEventId || files.length === 0}
                        className="w-full"
                        size="lg"
                    >
                        {isUploading ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Enviando...
                            </>
                        ) : (
                            <>
                                <Upload className="h-4 w-4 mr-2" />
                                Enviar {files.length} Foto(s)
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
}
